import type {
  AnswerShareCreatePayload,
  AnswerShareManageState,
  AnswerSharePatchPayload,
  AnswerSharePublicInfo,
  AnswerShareUnlockResponse,
} from "../types/answerShare";
import type {
  SubmissionsExportColumns,
  SubmissionsExportParams,
  SubmissionsListParams,
  SubmissionsListResponse,
} from "../types/submission";
import { downloadBlob } from "../lib/http/downloadBlob";
import { fetchAllByPage } from "../lib/http/fetchAllPages";
import {
  normalizeAnswerShareManage,
  normalizeAnswerSharePublic,
  normalizeAnswerShareUnlock,
} from "../lib/answerShare/normalize";
import { normalizeSubmissionsExportColumns } from "../lib/forms/normalizeExportColumns";
import { normalizeSubmissionsList } from "../lib/forms/normalizeSubmission";
import { buildSubmissionsExportQuery } from "../lib/forms/submissionsExport";
import { apiDownload, apiRequest } from "./client";

const SUBMISSIONS_FETCH_PAGE_SIZE = 100;

function managePath(formId: number): string {
  return `/api/v1/forms/${formId}/answer-share`;
}

function sharePath(token: string): string {
  return `/api/v1/answer-shares/${encodeURIComponent(token)}`;
}

/** GET /forms/{id}/answer-share */
export function fetchAnswerShare(
  formId: number,
): Promise<AnswerShareManageState> {
  return apiRequest<unknown>(managePath(formId)).then(
    normalizeAnswerShareManage,
  );
}

/** POST /forms/{id}/answer-share — создать или заменить. */
export function createAnswerShare(
  formId: number,
  payload: AnswerShareCreatePayload,
): Promise<AnswerShareManageState> {
  return apiRequest<unknown>(managePath(formId), {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(normalizeAnswerShareManage);
}

/** PATCH /forms/{id}/answer-share */
export function patchAnswerShare(
  formId: number,
  payload: AnswerSharePatchPayload,
): Promise<AnswerShareManageState> {
  return apiRequest<unknown>(managePath(formId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then(normalizeAnswerShareManage);
}

/** DELETE /forms/{id}/answer-share */
export function deleteAnswerShare(formId: number): Promise<void> {
  return apiRequest<void>(managePath(formId), { method: "DELETE" });
}

/** GET /answer-shares/{token} — публично, без ответов. */
export function fetchAnswerSharePublic(
  token: string,
): Promise<AnswerSharePublicInfo> {
  return apiRequest<unknown>(sharePath(token), {
    auth: false,
    skipUnauthorizedHandler: true,
  }).then(normalizeAnswerSharePublic);
}

/** POST /answer-shares/{token}/unlock */
export function unlockAnswerShare(
  token: string,
  password: string,
): Promise<AnswerShareUnlockResponse> {
  return apiRequest<unknown>(`${sharePath(token)}/unlock`, {
    method: "POST",
    auth: false,
    skipUnauthorizedHandler: true,
    body: JSON.stringify({ password }),
  }).then(normalizeAnswerShareUnlock);
}

type ShareRequestOptions = {
  accessToken: string;
};

/** GET /answer-shares/{token}/submissions */
export function fetchAnswerShareSubmissions(
  token: string,
  params: SubmissionsListParams & ShareRequestOptions,
): Promise<SubmissionsListResponse> {
  const { accessToken, page = 1, count = 15 } = params;
  const query = new URLSearchParams({
    page: String(page),
    count: String(count),
  });

  return apiRequest<unknown>(`${sharePath(token)}/submissions?${query}`, {
    auth: false,
    bearerToken: accessToken,
    skipUnauthorizedHandler: true,
  }).then((body) => {
    const data = normalizeSubmissionsList(body);
    const meta =
      body && typeof body === "object" && "meta" in body
        ? ((body as SubmissionsListResponse).meta ?? undefined)
        : undefined;

    return { data, meta, links: undefined };
  });
}

export async function fetchAllAnswerShareSubmissions(
  token: string,
  accessToken: string,
): Promise<SubmissionsListResponse["data"]> {
  return fetchAllByPage(
    (page, count) =>
      fetchAnswerShareSubmissions(token, {
        accessToken,
        page,
        count,
      }).then((response) => response.data ?? []),
    SUBMISSIONS_FETCH_PAGE_SIZE,
  );
}

/** GET /answer-shares/{token}/submissions/export/columns */
export function fetchAnswerShareExportColumns(
  token: string,
  accessToken: string,
): Promise<SubmissionsExportColumns> {
  return apiRequest<unknown>(`${sharePath(token)}/submissions/export/columns`, {
    auth: false,
    bearerToken: accessToken,
    skipUnauthorizedHandler: true,
  }).then((body) => normalizeSubmissionsExportColumns(body));
}

/** GET /answer-shares/{token}/submissions/export */
export async function exportAnswerShareSubmissions(
  token: string,
  accessToken: string,
  params: SubmissionsExportParams = {},
): Promise<void> {
  const query = buildSubmissionsExportQuery(params);
  const { blob, filename } = await apiDownload(
    `${sharePath(token)}/submissions/export${query}`,
    {
      auth: false,
      bearerToken: accessToken,
      skipUnauthorizedHandler: true,
    },
  );
  downloadBlob(blob, filename);
}
